import { CompassIcon, MapPinIcon, Navigation2Icon, RouteIcon } from "lucide-react";
import type { RouteCardRoute, RouteDetailItem, RouteKPI, RouteListItem, RouteType, Waypoint } from "./routes.types";

// ── Type derivation ───────────────────────────────────────────────────────────

function inferType(distanceNm: number): RouteType {
  if (distanceNm > 1000) return "ocean";
  if (distanceNm > 200) return "offshore";
  return "coastal";
}

function formatDuration(distanceNm: number): string {
  if (distanceNm === 0) return "—";
  const hours = distanceNm / 6; // ~6kt average
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours % 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

function toChartPoints(waypoints: RouteListItem["waypoints"]): Array<[number, number]> {
  const sorted = [...waypoints].sort((a, b) => a.orderIndex - b.orderIndex);
  if (sorted.length < 2)
    return [
      [0, 0.5],
      [1, 0.5],
    ];

  const lats = sorted.map((w) => w.lat);
  const lngs = sorted.map((w) => w.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latRange = maxLat - minLat || 1;
  const lngRange = maxLng - minLng || 1;

  return sorted.map((w) => [(w.lng - minLng) / lngRange, 1 - (w.lat - minLat) / latRange]);
}

// ── Public adapter ────────────────────────────────────────────────────────────
//
// TODO: Several RouteCardRoute fields are missing from the backend Route type
// and are derived/faked here. Revisit when the schema evolves:
//
//   departurePort / arrivalPort — schema has no port field; using first/last
//     waypoint name as a fallback. Should be explicit fields on Route.
//
//   type (ocean/offshore/coastal) — inferred from totalDistanceNm thresholds.
//     Should be a proper enum stored on the route by the user.
//
//   estimatedDuration — computed at a fixed 6kt average.
//     Should come from the backend (user-set or calculated from distance + speed).
//
//   expeditionCount — not in the Route type at all; requires a separate join
//     query (expeditions → routeId). Hardcoded to 0 for now.
//
//   lastUsed — not in the schema. Would need the most recent expedition
//     start date for this route. Hardcoded to null for now.

export function toRouteCardRoute(r: RouteListItem): RouteCardRoute {
  const sorted = [...r.waypoints].sort((a, b) => a.orderIndex - b.orderIndex);
  const distNm = r.totalDistanceNm ?? 0;

  return {
    id: r.id,
    name: r.name,
    departurePort: sorted[0]?.name ?? "Start",
    arrivalPort: sorted.at(-1)?.name ?? "End",
    chartPoints: toChartPoints(r.waypoints),
    distance: Math.round(distNm),
    waypointCount: sorted.length,
    estimatedDuration: formatDuration(distNm),
    type: inferType(distNm),
    expeditionCount: 0,
    lastUsed: null,
    createdAt: new Date(r.createdAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
  };
}

// ── Route detail adapter ──────────────────────────────────────────────────────

/** Haversine distance in nautical miles between two lat/lng points */
function haversineNm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3440.065; // Earth radius in nm
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Format decimal degrees to a human-readable DMS string */
function fmtDeg(deg: number, pos: "lat" | "lng"): string {
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const mFull = (abs - d) * 60;
  const m = Math.floor(mFull);
  const suffix = pos === "lat" ? (deg >= 0 ? "N" : "S") : deg >= 0 ? "E" : "W";
  return `${d}°${String(m).padStart(2, "0")}'${suffix}`;
}

export interface RouteDetailView {
  id: string;
  name: string;
  description: string;
  type: RouteType;
  totalDistance: number;
  estimatedDuration: string;
  /** Avg sailing speed assumption (kt) used for duration estimate */
  averageSpeed: number;
  createdAt: string;
  waypoints: Waypoint[];
  /** [lng, lat] pairs for the map polyline */
  mapRoute: Array<[number, number]>;
}

// TODO: RouteDetailItem has no `expeditionCount` or linked expedition data —
//   needs a separate query join (expeditions → routeId). Tracked for schema work.

export function toRouteDetailView(r: RouteDetailItem): RouteDetailView {
  const sorted = [...r.waypoints].sort((a, b) => a.orderIndex - b.orderIndex);
  const distNm = r.totalDistanceNm ?? 0;

  const waypoints: Waypoint[] = sorted.map((wp, i) => {
    const prev = sorted[i - 1];
    const distFromPrev = prev != null ? Math.round(haversineNm(prev.lat, prev.lng, wp.lat, wp.lng)) : null;
    const kind = i === 0 ? "depart" : i === sorted.length - 1 ? "arrive" : "waypoint";
    return {
      id: wp.id,
      name: wp.name ?? `Waypoint ${i + 1}`,
      lat: fmtDeg(wp.lat, "lat"),
      lng: fmtDeg(wp.lng, "lng"),
      kind,
      distanceFromPrev: distFromPrev,
    };
  });

  return {
    id: r.id,
    name: r.name,
    description: r.description ?? "",
    type: inferType(distNm),
    totalDistance: Math.round(distNm),
    estimatedDuration: formatDuration(distNm),
    averageSpeed: 6,
    createdAt: new Date(r.createdAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
    waypoints,
    mapRoute: sorted.map((wp) => [wp.lng, wp.lat]),
  };
}

// ── KPI builder ───────────────────────────────────────────────────────────────

export function buildRouteKPIs(routes: RouteListItem[]): RouteKPI[] {
  const totalNm = routes.reduce((sum, r) => sum + (r.totalDistanceNm ?? 0), 0);
  const totalWaypoints = routes.reduce((sum, r) => sum + r.waypoints.length, 0);
  const longest = [...routes].sort((a, b) => (b.totalDistanceNm ?? 0) - (a.totalDistanceNm ?? 0))[0];

  return [
    {
      label: "Routes Saved",
      value: String(routes.length),
      sub: "in your library",
      Icon: RouteIcon,
      accentClass: "bg-abyss-500",
      iconBg: "bg-abyss-50 dark:bg-abyss-800",
      iconColor: "text-abyss-500",
    },
    {
      label: "Total Distance",
      value: Math.round(totalNm).toLocaleString(),
      sub: "nm plotted",
      Icon: Navigation2Icon,
      accentClass: "bg-shallow-500",
      iconBg: "bg-shallow-50 dark:bg-shallow-900",
      iconColor: "text-shallow-600",
    },
    {
      label: "Total Waypoints",
      value: String(totalWaypoints),
      sub: "across all routes",
      Icon: MapPinIcon,
      accentClass: "bg-signal-500",
      iconBg: "bg-signal-50 dark:bg-signal-900",
      iconColor: "text-signal-600",
    },
    {
      label: "Longest Route",
      value: longest ? longest.name.split(" ").slice(0, 2).join(" ") : "—",
      sub: longest ? `${Math.round(longest.totalDistanceNm ?? 0).toLocaleString()} nm` : "no routes yet",
      Icon: CompassIcon,
      accentClass: "bg-violet-500",
      iconBg: "bg-violet-50 dark:bg-violet-900",
      iconColor: "text-violet-600",
    },
  ];
}
